import type { LocationContent } from "@/components/location-page";
import { FEES, FIRM } from "@/lib/firm";

export const SUNSHINE_COAST_HUB: LocationContent = {
  slug: "conveyancing-sunshine-coast",
  name: "the Sunshine Coast",
  title: "Sunshine Coast conveyancing: fixed-fee solicitors",
  metaTitle: "Conveyancing Sunshine Coast | Coastside Law",
  description: "Buying or selling on the Sunshine Coast? Golden Beach solicitors, fixed professional fees and free pre-signing contract review. Caloundra to Noosa.",
  lead: `A Sunshine Coast law practice based at Golden Beach, Caloundra — ${FEES.sale} to sell and ${FEES.purchase} to buy, both including GST, with free contract review before you sign.`,
  paragraphs: [
    "Coastside Law acts for buyers and sellers from Caloundra and Golden Beach through to Mooloolaba, Maroochydore, Buderim and Noosa. Michael Klein has been a solicitor since 2003 and Erin Wilkinson was an experienced South East Queensland conveyancer before her admission in 2025 — over 40 years of combined experience and thousands of Queensland settlements between them.",
    "Sunshine Coast transactions are rarely uniform. Waterfront and canal lots carry revetment wall and jetty obligations; the beachfront towers are large body corporate schemes with letting agreements and defect histories; the hinterland brings acreage, bores, septic and vegetation issues; and there is a constant flow of off-the-plan contracts in Maroochydore and the newer estates.",
    "You deal with a lawyer, not a processing centre. If a contract goes into dispute, a caveat appears, or the seller has died, that work stays with us.",
  ],
  localNotes: [
    {
      t: "Body corporate and holiday letting",
      d: "For a Kings Beach or Mooloolaba unit, ask about levies, insurance, upcoming works and letting arrangements. Proposed holiday letting needs property-specific checks of the records and applicable planning and use requirements; a listing's income estimate is not permission to operate.",
    },
    {
      t: "Coastal hazard and erosion overlays",
      d: "Erosion prone areas and coastal hazard overlays affect what can be built and rebuilt near the beach at Golden Beach, Kings Beach, Dicky Beach and Marcoola. We check the overlays against what you intend to do with the property.",
    },
    {
      t: "Canal and waterfront lots",
      d: "For a Pelican Waters canal home, check title rights, approvals and conditions for waterfront structures, and who maintains them. Legal records do not prove a pontoon or revetment wall is sound; specialist inspection may be needed.",
    },
    {
      t: "Off the plan in Maroochydore and new estates",
      d: "Sunset dates, variation rights, disclosure updates and the risk of valuation falling short at completion — all of it should be understood before you commit, not two years later.",
    },
    {
      t: "Hinterland and acreage",
      d: "Buderim, Palmwoods and the hinterland bring slope and landslide overlays, on-site sewerage, bore and water access, and vegetation clearing restrictions. Different searches, different risks.",
    },
    {
      t: "Downsizing and estate sales",
      d: "A move from a Golden Beach house to a Caloundra unit can raise ownership and estate-planning questions as well as settlement arrangements. For an estate sale, check the seller's authority and any required grant before agreeing to dates.",
    },
  ],
  suburbs: [
    "Caloundra",
    "Golden Beach",
    "Pelican Waters",
    "Currimundi",
    "Kawana",
    "Mooloolaba",
    "Maroochydore",
    "Buderim",
    "Noosa",
    "Coolum Beach",
    "Peregian Beach",
    "Beerwah",
  ],
  faqs: [
    {
      q: "What changes if I am selling a deceased estate property?",
      a: "The title, will, executor's authority and any required grant need checking before you commit to a contract or settlement date. A solely owned home and a jointly owned home may need different processes. Probate is only one stage; debts, tax, claims and estate administration may still need attention.",
    },
    {
      q: "What should I check when buying a Pelican Waters waterfront home?",
      a: "Ask about the title and plan, waterfront access, approvals and conditions for pontoons or other structures, and maintenance responsibilities. Legal searches do not establish structural condition: a wall or pontoon may also need specialist inspection. Tell us about your intended boat use or alterations before signing.",
    },
    {
      q: "Are the checks different for a coastal apartment or hinterland property?",
      a: "Yes. An apartment can raise body corporate, levy, by-law and proposed-use questions. Acreage may need enquiries about access, on-site sewerage, water supply and planning restrictions. The property and your intended use determine the enquiries; a standard search list is not a substitute for discussing your plans.",
    },
    {
      q: "Is your Sunshine Coast conveyancing fixed price?",
      a: `Our standard conveyancing professional fees are ${FEES.sale} to sell and ${FEES.purchase} to buy, both including GST, agreed before we start. Searches, electronic settlement, registration and transfer duty are separate. Ask for the written scope and an estimate of additional costs so you can compare the total.`,
    },
    {
      q: "Are you a conveyancer or a solicitor?",
      a: "Both. Your conveyancing is handled by solicitors, so if something goes wrong mid-contract, such as a dispute, a caveat or a deceased seller, the same team can give legal advice instead of referring you elsewhere.",
    },
    {
      q: "Where is your Sunshine Coast office?",
      a: `Our principal office is at ${FIRM.street}, ${FIRM.suburb}, Caloundra, with a second office on the Redcliffe Peninsula. Settlements are electronic, so most clients never need to attend either — but you are welcome to.`,
    },
    {
      q: "How much does conveyancing cost on the Sunshine Coast?",
      a: `${FEES.sale} ${FEES.saleNote} for a sale and ${FEES.purchase} ${FEES.purchaseNote} for a purchase, as a fixed professional fee. Searches, PEXA and registration fees and transfer duty are additional and charged at cost.`,
    },
    {
      q: "Do you review contracts before signing?",
      a: "Yes, at no charge. And if a contract we act on terminates under cooling-off, finance or building and pest, no professional fee is charged — you pay only disbursements actually incurred.",
    },
    {
      q: "Do you act on unit and apartment purchases?",
      a: "Yes. Ask us to scope the body corporate enquiries, including levies, financial records, insurance, by-laws, recorded defects or disputes and letting arrangements. Records may take time to obtain. Discuss the necessary contract conditions and deadlines before signing rather than assuming every enquiry can be completed during cooling-off.",
    },
  ],
};
